// Import React helpers.
import React, { useMemo, useEffect, useState } from "react";

// Import strich api calls
import {
    getTerms,
} from "api/APICoreCalls";

// Import hooks
import { UseScrollingSections } from "global/hooks/useScrollingSections";

// Import policy page components.
import { PolicyNavigation } from "./components/PolicyNavigation";
import { PolicySection } from "./components/PolicySection";
import { LandingHeader } from "../components/LandingHeader";
import { LandingFooter } from "../components/LandingFooter";
import { BackToTopButton } from "global/components/BackToTopButton";

// Import the page stylesheet.
import "./PolicyPage.css";



// Export the policy page.
const LandingPolicyPage = () => {


    // Set empty array state for selected connection
    const [policies, setPolicies] = useState([]);



    // Fetch connections once on mount
    useEffect(() => {

        // run the async function
        fetchPolicies();

    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    

    // Fetch the policies to display from the footer
    const fetchPolicies = async () => {

        // Store the response
        const policiesResponse = await getTerms("privacy");

        if (policiesResponse.success) {
       
            // Update the state
            setPolicies(policiesResponse.results);

         }

    };



    // Sort the policy sections into their API-defined order.
    const sortedPolicies = useMemo(() => {

        // Return an empty array when policies have not loaded.
        if (!Array.isArray(policies)) {

            // Return the safe fallback.
            return [];

        }

        // Copy and sort the policies without mutating API data.
        return [...policies].sort((firstPolicy, secondPolicy) => {

            // Sort using the API ordering value.
            return Number(firstPolicy?.ordering ?? 0) - Number(secondPolicy?.ordering ?? 0);

        });

    }, [policies]);



    // Set up reusable section scrolling behaviour.
    const {
        activeSectionKey,
        handleSectionSelect,
        navigationScrollRef,
    } = UseScrollingSections({

        // Supply the policy sections.
        sections: sortedPolicies,

        // Tell the hook how to get a unique section key.
        getSectionKey: (policy) => policy?.section_key,

    });


    
    // Display an API error.
    if (!policies) {

        // Render the error message.
        return (

            // Render the policy page error state.
            <div className="policy-page__status policy-page__status--error">
                Could not load policies.
            </div>

        );

    }

  

    // Render the policy page.
    return (

        // Render the complete policy page.
        <div className="landing-page">

            {/* Render the site's top navigation. */}
            <LandingHeader
                logo={{
                    // Set the existing StrichSuite logo image.
                    src: "/images/StrichSuite-logo.png",

                    // Provide the logo alternative text.
                    alt: "StrichSuite",
                }}
                signInUrl={"/login"}
                createAccountUrl={"/register"}
                displayFAQ={false}
            />

            
            {/* Render the main policy page content. */}
            <main className="policy-page__main">

                {/* Render the policy introduction. */}
                <div className="policy-page__introduction">

                    {/* Render the policy title. */}
                    <h1 className="policy-page__title">
                        Privacy Policy
                    </h1>

                    {/* Explain the document below. */}
                    <p className="policy-page__subtitle">
                        How StrichSuite collects, uses and protects your information.
                    </p>

                </div>

                {/* Render the navigation and policy content layout. */}
                <div className="policy-page__layout">

                    {/* Render the policy section navigation. */}
                    <PolicyNavigation
                        policies={sortedPolicies}
                        activeSectionKey={activeSectionKey}
                        onSectionSelect={handleSectionSelect}
                        navigationScrollRef={navigationScrollRef}
                    />

                    {/* Render all policy sections. */}
                    <div className="policy-page__content">

                        {/* Loop through each policy section. */}
                        {sortedPolicies.map((policy) => (

                            // Render the individual policy section.
                            <PolicySection
                                key={policy?.section_key}
                                policy={policy}
                            />

                        ))}

                    </div>

                </div>

            </main>

            {/* Render the floating back-to-top button. */}
            <BackToTopButton />

            {/* Render the global site footer. */}
            <LandingFooter policies={policies} />

        </div>

    );

}; export default LandingPolicyPage;